import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, Loader2, Plus, RefreshCw, Trash2, Pencil, Mic, ExternalLink, Video, Upload, Image as ImageIcon, MessageSquareQuote } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const mediaUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

const EMPTY = { title: "", speaker: "Dr. Jeannelle Perkins, LMFT-S, EMDR", event_name: "",
                 date: "", venue: "", city: "", description: "", event_url: "",
                 recording_url: "", slides_url: "", is_virtual: false };

export default function PlatformSpeaking() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/speaking-engagements"); setData(r.data); }
    catch { toast.error("Could not load engagements."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.date) { toast.error("Title and date are required"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, event_url: form.event_url || null,
                         recording_url: form.recording_url || null,
                         slides_url: form.slides_url || null };
      if (editingId) {
        await api.patch(`/platform/speaking-engagements/${editingId}`, payload);
        toast.success("Engagement updated.");
      } else {
        await api.post("/platform/speaking-engagements", payload);
        toast.success("Engagement added.");
      }
      setForm(EMPTY); setEditingId(null); setShowForm(false); await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setSubmitting(false); }
  };

  const startEdit = (e) => {
    setForm({ title: e.title || "", speaker: e.speaker || "Dr. Jeannelle Perkins, LMFT-S, EMDR",
              event_name: e.event_name || "", date: e.date || "",
              venue: e.venue || "", city: e.city || "",
              description: e.description || "", event_url: e.event_url || "",
              recording_url: e.recording_url || "", slides_url: e.slides_url || "",
              is_virtual: !!e.is_virtual });
    setEditingId(e.engagement_id); setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this engagement?")) return;
    try { await api.delete(`/platform/speaking-engagements/${id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };
  const cancel = () => { setForm(EMPTY); setEditingId(null); setShowForm(false); };

  const Row = ({ e, past }) => (
    <div data-testid={`admin-speaking-row-${e.engagement_id}`}
         className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-4">
      <div className="flex-1">
        <div className="text-xs text-muted-foreground">{e.date} {past && "· past"}</div>
        <div className="font-medium">{e.title}</div>
        {e.event_name && <div className="text-xs text-muted-foreground">{e.event_name}</div>}
        {(e.venue || e.city) && <div className="text-xs text-muted-foreground">{[e.venue, e.city].filter(Boolean).join(" · ")}</div>}
        {e.event_url && (
          <a href={e.event_url} target="_blank" rel="noreferrer"
             className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-600 hover:underline">
            <ExternalLink className="h-3 w-3" /> {e.event_url}
          </a>
        )}
      </div>
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" data-testid={`admin-speaking-edit-${e.engagement_id}`}
                onClick={() => startEdit(e)}><Pencil className="h-3 w-3" /></Button>
        <Button variant="ghost" size="sm" data-testid={`admin-speaking-delete-${e.engagement_id}`}
                onClick={() => remove(e.engagement_id)}><Trash2 className="h-3 w-3 text-red-500" /></Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="rounded-md p-1.5" style={{ background: "hsl(var(--haruki-teal))" }}>
              <Mic className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-heading text-base font-bold tracking-tight">Haruki Health</div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "hsl(var(--haruki-teal))" }}>
                Admin · Speaking engagements
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/platform" className="text-xs text-muted-foreground hover:text-foreground">
              <ArrowLeft className="mr-1 inline h-3 w-3" />Platform overview
            </Link>
            <a href="/speaking" target="_blank" rel="noreferrer"
               className="text-xs text-emerald-600 hover:underline"
               data-testid="admin-speaking-view-public">
              View public page <ExternalLink className="ml-0.5 inline h-3 w-3" />
            </a>
            <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/"); }}>
              <LogOut className="mr-2 h-3 w-3" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold">Speaking engagements</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Publish Dr. Jeannelle&apos;s appearances. Live at <code className="rounded bg-muted px-1">/speaking</code>.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load}><RefreshCw className="mr-1.5 h-3 w-3" /> Refresh</Button>
            <Button size="sm" onClick={() => { setForm(EMPTY); setEditingId(null); setShowForm(true); }}
                    data-testid="admin-speaking-new">
              <Plus className="mr-1.5 h-3 w-3" /> New engagement
            </Button>
          </div>
        </div>

        {showForm && (
          <form onSubmit={submit} className="mt-6 grid gap-4 rounded-lg border border-border bg-card p-5"
                data-testid="admin-speaking-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="title">Talk title *</Label>
                <Input id="title" required value={form.title}
                       data-testid="admin-speaking-title"
                       onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input id="date" type="date" required value={form.date}
                       data-testid="admin-speaking-date"
                       onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="event_name">Event / conference</Label>
                <Input id="event_name" value={form.event_name}
                       placeholder="AAMFT Global Systemic Therapy Summit"
                       onChange={(e) => setForm({ ...form, event_name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="venue">Venue</Label>
                <Input id="venue" value={form.venue}
                       placeholder="Bethel Boston Church"
                       onChange={(e) => setForm({ ...form, venue: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="city">City / region</Label>
                <Input id="city" value={form.city}
                       placeholder="Boston, MA"
                       onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="url">Event URL</Label>
                <Input id="url" type="url" value={form.event_url}
                       placeholder="https://…"
                       onChange={(e) => setForm({ ...form, event_url: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="rec">Recording URL <span className="text-muted-foreground">(after event)</span></Label>
                <Input id="rec" type="url" value={form.recording_url}
                       data-testid="admin-speaking-recording"
                       placeholder="https://youtu.be/…"
                       onChange={(e) => setForm({ ...form, recording_url: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="slides">Slides URL <span className="text-muted-foreground">(after event)</span></Label>
                <Input id="slides" type="url" value={form.slides_url}
                       data-testid="admin-speaking-slides"
                       placeholder="https://…"
                       onChange={(e) => setForm({ ...form, slides_url: e.target.value })} />
              </div>
            </div>
            <div>
              <Label htmlFor="desc">Description</Label>
              <textarea id="desc" rows={3} value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_virtual}
                     onChange={(e) => setForm({ ...form, is_virtual: e.target.checked })} />
              Virtual event
            </label>
            <div className="flex gap-2">
              <Button type="submit" disabled={submitting} data-testid="admin-speaking-submit">
                {submitting ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Plus className="mr-2 h-3 w-3" />}
                {editingId ? "Save changes" : "Publish"}
              </Button>
              <Button type="button" variant="ghost" onClick={cancel}>Cancel</Button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="mt-8 text-sm text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading…</div>
        ) : (
          <>
            <h2 className="mt-8 font-heading text-sm font-bold uppercase tracking-widest text-muted-foreground">
              Upcoming · {data.upcoming.length}
            </h2>
            <div className="mt-3 space-y-2">
              {data.upcoming.length === 0
                ? <div className="text-sm text-muted-foreground">Nothing scheduled.</div>
                : data.upcoming.map(e => <Row key={e.engagement_id} e={e} />)}
            </div>
            {data.past.length > 0 && (
              <>
                <h2 className="mt-10 font-heading text-sm font-bold uppercase tracking-widest text-muted-foreground">
                  Past · {data.past.length}
                </h2>
                <div className="mt-3 space-y-2">
                  {data.past.map(e => <Row key={e.engagement_id} e={e} past />)}
                </div>
              </>
            )}
          </>
        )}

        <SpeakerReelEditor />
        <TestimonialsEditor />
      </main>
    </div>
  );
}


// ============================================================================
// Speaker Reel editor — URL paste + MP4 upload; either supersedes the other
// ============================================================================
function SpeakerReelEditor() {
  const [reel, setReel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [posterBusy, setPosterBusy] = useState(false);
  const fileInput = useRef(null);
  const posterInput = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/speaker-reel"); setReel(r.data?.reel || null); }
    catch { toast.error("Could not load speaker reel."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveUrl = async (e) => {
    e.preventDefault();
    if (!url.trim()) { toast.error("Paste a YouTube, Vimeo or direct MP4 URL first"); return; }
    setBusy(true);
    try {
      await api.post("/platform/speaker-reel/url", { url: url.trim() });
      toast.success("Speaker reel saved.");
      setUrl(""); await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 60 * 1024 * 1024) { toast.error("File is over 60 MB. Please compress first."); return; }
    setBusy(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/platform/speaker-reel/upload", fd, {
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      toast.success("Reel uploaded.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setBusy(false); setProgress(0); if (fileInput.current) fileInput.current.value = ""; }
  };

  const uploadPoster = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Poster must be under 5 MB"); return; }
    setPosterBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/platform/speaker-reel/poster", fd);
      toast.success("Poster image set.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Poster upload failed.");
    } finally { setPosterBusy(false); if (posterInput.current) posterInput.current.value = ""; }
  };

  const clearPoster = async () => {
    if (!window.confirm("Remove the custom poster? The auto fallback (YouTube thumbnail or portrait) will show.")) return;
    try { await api.delete("/platform/speaker-reel/poster"); toast.success("Poster removed."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to clear poster."); }
  };

  const clear = async () => {
    if (!window.confirm("Remove the current speaker reel? The portrait will show instead.")) return;
    try { await api.delete("/platform/speaker-reel"); toast.success("Reel cleared."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Clear failed."); }
  };

  return (
    <section className="mt-14" data-testid="admin-reel-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold">Speaker reel</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            A short video that plays in the hero of <code className="rounded bg-muted px-1">/speaking</code>.
            Paste a YouTube/Vimeo link <em>or</em> upload an MP4 — whichever you set most recently wins.
          </p>
        </div>
        {reel && (
          <Button variant="outline" size="sm" onClick={clear} data-testid="admin-reel-clear">
            <Trash2 className="mr-1.5 h-3 w-3 text-red-500" /> Remove reel
          </Button>
        )}
      </div>

      <div className="mt-5 grid gap-6 lg:grid-cols-[280px_1fr]">
        {/* Preview */}
        <div className="rounded-lg border border-border bg-card p-4">
          {loading ? (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !reel ? (
            <div className="flex h-40 flex-col items-center justify-center text-center text-xs text-muted-foreground">
              <Video className="mb-2 h-6 w-6 opacity-50" />
              No reel set. The portrait photo will show.
            </div>
          ) : reel.kind === "upload" ? (
            <div>
              <video src={mediaUrl(reel.stream_url)} controls preload="metadata"
                     data-testid="admin-reel-preview-video"
                     poster={mediaUrl(reel.poster_url) || undefined}
                     className="w-full rounded-md" style={{ aspectRatio: "9 / 12", objectFit: "cover" }} />
              <div className="mt-2 truncate text-xs text-muted-foreground">
                Uploaded · {reel.original_filename} · {(reel.size / 1024 / 1024).toFixed(1)} MB
              </div>
            </div>
          ) : reel.provider === "youtube" || reel.provider === "vimeo" ? (
            <div>
              <iframe src={reel.embed_url} title="Reel"
                       data-testid="admin-reel-preview-iframe"
                       className="w-full rounded-md" style={{ aspectRatio: "9 / 12" }}
                       allow="encrypted-media" />
              <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                <ExternalLink className="h-3 w-3" /> {reel.provider} · <a href={reel.url} target="_blank" rel="noreferrer" className="truncate underline">{reel.url}</a>
              </div>
            </div>
          ) : (
            <div>
              <video src={reel.url} controls preload="metadata"
                     className="w-full rounded-md" style={{ aspectRatio: "9 / 12", objectFit: "cover" }} />
              <div className="mt-2 truncate text-xs text-muted-foreground">Direct URL · {reel.url}</div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="space-y-6">
          <form onSubmit={saveUrl} className="rounded-lg border border-border bg-card p-4"
                data-testid="admin-reel-url-form">
            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Option A · Paste a video URL
            </Label>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <Input type="url" placeholder="https://www.youtube.com/watch?v=…"
                     value={url} onChange={(e) => setUrl(e.target.value)}
                     data-testid="admin-reel-url-input" />
              <Button type="submit" disabled={busy} data-testid="admin-reel-url-save">
                {busy ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <ExternalLink className="mr-2 h-3 w-3" />}
                Save URL
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              YouTube (watch, embed, shorts, youtu.be), Vimeo, or a direct MP4/MOV/WebM link.
            </p>
          </form>

          <div className="rounded-lg border border-dashed border-border bg-card p-4">
            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Option B · Upload a file
            </Label>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <input ref={fileInput} type="file"
                      accept="video/mp4,video/quicktime,video/webm"
                      data-testid="admin-reel-file-input"
                      onChange={(e) => uploadFile(e.target.files?.[0])}
                      className="text-xs" />
              {busy && progress > 0 && (
                <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> Uploading… {progress}%
                </span>
              )}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              MP4, MOV or WebM · max 60 MB. Portrait orientation (9:16) plays best in the hero.
            </p>
          </div>

          {reel && (
            <div className="rounded-lg border border-border bg-card p-4"
                 data-testid="admin-reel-poster-section">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    Poster image <span className="ml-1 text-muted-foreground/70 normal-case tracking-normal">(the still shown before play)</span>
                  </Label>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Overrides the {reel.kind === "upload" ? "portrait fallback" : "auto thumbnail"}. PNG, JPEG or WEBP · max 5 MB.
                  </p>
                </div>
                {reel.poster_storage_path && (
                  <Button variant="ghost" size="sm" onClick={clearPoster}
                          data-testid="admin-reel-poster-clear">
                    <Trash2 className="mr-1.5 h-3 w-3 text-red-500" /> Remove
                  </Button>
                )}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-4">
                {reel.poster_url ? (
                  <img src={mediaUrl(reel.poster_url)} alt="Reel poster"
                       data-testid="admin-reel-poster-preview"
                       className="h-24 w-16 rounded-md border border-border bg-muted object-cover" />
                ) : (
                  <div className="flex h-24 w-16 items-center justify-center rounded-md border border-dashed border-border bg-muted/40 text-[10px] text-muted-foreground">
                    No custom poster
                  </div>
                )}
                <input ref={posterInput} type="file"
                        accept="image/png,image/jpeg,image/webp"
                        data-testid="admin-reel-poster-input"
                        onChange={(e) => uploadPoster(e.target.files?.[0])}
                        className="text-xs" />
                {posterBusy && (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                )}
                {reel.poster_storage_path && (
                  <span className="text-[10px] font-medium uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
                    Custom poster active
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}


// ============================================================================
// Testimonials editor
// ============================================================================
const EMPTY_TESTI = { quote: "", author_name: "", author_role: "", org_name: "",
                       org_logo_url: "", order: 0, is_active: true };

function TestimonialsEditor() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_TESTI);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/platform/testimonials"); setRows(r.data?.testimonials || []); }
    catch { toast.error("Could not load testimonials."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.quote.trim() || !form.author_name.trim()) {
      toast.error("Quote and author name are required."); return;
    }
    setBusy(true);
    try {
      const payload = { ...form,
        org_logo_url: form.org_logo_url?.trim() || null,
        author_role: form.author_role?.trim() || null,
        org_name: form.org_name?.trim() || null,
        order: Number(form.order) || 0,
      };
      if (editingId) {
        await api.patch(`/platform/testimonials/${editingId}`, payload);
        toast.success("Testimonial updated.");
      } else {
        await api.post("/platform/testimonials", payload);
        toast.success("Testimonial added.");
      }
      setForm(EMPTY_TESTI); setEditingId(null); setShowForm(false); await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const startEdit = (t) => {
    setForm({ quote: t.quote || "", author_name: t.author_name || "",
              author_role: t.author_role || "", org_name: t.org_name || "",
              org_logo_url: t.org_logo_url || "",
              order: t.order || 0, is_active: t.is_active !== false });
    setEditingId(t.testimonial_id); setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await api.delete(`/platform/testimonials/${id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  const uploadLogo = async (id, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/platform/testimonials/${id}/logo`, fd);
      toast.success("Logo uploaded.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed.");
    }
  };

  return (
    <section className="mt-14" data-testid="admin-testimonials-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold">Testimonials</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Quotes from past hosts (Bethel Boston, AAMFT, etc.) shown under the events grid.
          </p>
        </div>
        <Button size="sm" onClick={() => { setForm(EMPTY_TESTI); setEditingId(null); setShowForm(true); }}
                data-testid="admin-testi-new">
          <Plus className="mr-1.5 h-3 w-3" /> New testimonial
        </Button>
      </div>

      {showForm && (
        <form onSubmit={submit} data-testid="admin-testi-form"
              className="mt-4 grid gap-3 rounded-lg border border-border bg-card p-5">
          <div>
            <Label htmlFor="t-quote">Quote *</Label>
            <textarea id="t-quote" required rows={3} value={form.quote}
                       data-testid="admin-testi-quote"
                       onChange={(e) => setForm({ ...form, quote: e.target.value })}
                       className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="t-name">Author name *</Label>
              <Input id="t-name" required value={form.author_name}
                     data-testid="admin-testi-name"
                     placeholder="Pastor John Smith"
                     onChange={(e) => setForm({ ...form, author_name: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="t-role">Author role</Label>
              <Input id="t-role" value={form.author_role}
                     placeholder="Senior Pastor"
                     onChange={(e) => setForm({ ...form, author_role: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="t-org">Organization</Label>
              <Input id="t-org" value={form.org_name}
                     data-testid="admin-testi-org"
                     placeholder="Bethel Boston Church"
                     onChange={(e) => setForm({ ...form, org_name: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="t-logo">Logo URL <span className="text-muted-foreground">(or upload after saving)</span></Label>
              <Input id="t-logo" type="url" value={form.org_logo_url}
                     placeholder="https://example.com/logo.png"
                     onChange={(e) => setForm({ ...form, org_logo_url: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="t-order">Display order</Label>
              <Input id="t-order" type="number" value={form.order}
                     onChange={(e) => setForm({ ...form, order: e.target.value })} />
            </div>
            <label className="mt-auto inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active}
                     onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Active (visible on <code className="rounded bg-muted px-1">/speaking</code>)
            </label>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy} data-testid="admin-testi-submit">
              {busy ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Plus className="mr-2 h-3 w-3" />}
              {editingId ? "Save changes" : "Publish"}
            </Button>
            <Button type="button" variant="ghost"
                    onClick={() => { setForm(EMPTY_TESTI); setEditingId(null); setShowForm(false); }}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      <div className="mt-5 space-y-3">
        {loading ? (
          <div className="text-sm text-muted-foreground">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            <MessageSquareQuote className="mx-auto mb-2 h-5 w-5 opacity-60" />
            No testimonials yet. Add quotes from past hosts to build social proof.
          </div>
        ) : rows.map(t => (
          <div key={t.testimonial_id}
               data-testid={`admin-testi-row-${t.testimonial_id}`}
               className={`flex items-start justify-between gap-4 rounded-lg border p-4 ${t.is_active ? "border-border bg-card" : "border-dashed border-border bg-card/50 opacity-70"}`}>
            <div className="flex flex-1 items-start gap-3">
              {t.logo_url ? (
                <img src={mediaUrl(t.logo_url)} alt={t.org_name || t.author_name}
                     className="h-10 w-10 shrink-0 rounded-md border border-border bg-white object-contain p-1" />
              ) : (
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-dashed border-border text-xs text-muted-foreground">
                  <ImageIcon className="h-4 w-4" />
                </span>
              )}
              <div className="flex-1">
                <div className="text-sm italic text-muted-foreground">&ldquo;{t.quote}&rdquo;</div>
                <div className="mt-1 text-xs">
                  <span className="font-semibold">{t.author_name}</span>
                  {(t.author_role || t.org_name) && (
                    <span className="text-muted-foreground"> · {[t.author_role, t.org_name].filter(Boolean).join(" · ")}</span>
                  )}
                  {!t.is_active && <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-widest">Hidden</span>}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <label className="cursor-pointer text-xs text-emerald-600 hover:underline"
                     data-testid={`admin-testi-logo-${t.testimonial_id}`}>
                <Upload className="mr-1 inline h-3 w-3" /> Upload logo
                <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
                       onChange={(e) => uploadLogo(t.testimonial_id, e.target.files?.[0])} />
              </label>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => startEdit(t)}
                        data-testid={`admin-testi-edit-${t.testimonial_id}`}>
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => remove(t.testimonial_id)}
                        data-testid={`admin-testi-delete-${t.testimonial_id}`}>
                  <Trash2 className="h-3 w-3 text-red-500" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
